<template>
  <div class="luminance-uniformity-report-detail-container">
    <!-- 1. 顶部图像预览 -->
    <div class="report-header-image pdf-atomic-block">
      <div class="image-label-wrapper">
        <el-tag type="primary" effect="dark">{{
          $t("report.originalImage")
        }}</el-tag>
      </div>
      <div class="image-wrapper">
        <div v-if="convertingMap.original" class="image-loading">
          <i class="el-icon-loading"></i>
        </div>
        <div v-else-if="conversionErrors.original" class="image-error">
          {{ $t("error.operationFailed") }}
        </div>
        <img
          v-else-if="displayOriginalImage"
          :src="displayOriginalImage"
          crossorigin="anonymous"
          alt="Test Report Preview"
        />
      </div>
    </div>

    <div class="pdf-atomic-block">
      <!-- 2. 分析结果 -->
      <h4 id="analysis-result" class="section-title">
        {{ $t("report.analysis_result_title") }}
      </h4>
      <div class="analysis-table-wrapper">
        <el-table
          v-if="reportResultData && reportResultData.result"
          :data="analysisTableData"
          border
          class="custom-table"
          style="width: 100%; max-width: 600px"
        >
          <el-table-column prop="item" label="Item" align="center" />
          <el-table-column prop="value" label="Value" align="center" />
        </el-table>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <!-- 3. 亮度分布图表 -->
      <h4 id="brightness-distribution" class="section-title">
        {{ $t("report.Luminance_distribution_title") }}
      </h4>
      <!-- Y(Luminance) - ECharts -->
      <h5 class="sub-section-title">{{ $t("report.y_luminance") }}</h5>
      <div class="chart-container">
        <v-chart
          class="echart-3d"
          :option="yLuminanceChartOption"
          ref="yLuminanceChartOption_Chart"
          :init-options="initOptions"
          autoresize
        />
      </div>
    </div>

    <div class="pdf-atomic-block">
      <!-- SNR(dB) - ECharts -->
      <h5 class="sub-section-title">{{ $t("report.snr_db") }}</h5>
      <div class="chart-container">
        <v-chart
          class="echart-3d"
          :option="snrChartOption"
          ref="snrChartOption_Chart"
          :init-options="initOptions"
          autoresize
        />
      </div>
    </div>

    <div class="pdf-atomic-block">
      <!-- Eab - ECharts -->
      <h5 class="sub-section-title">{{ $t("report.eab") }}</h5>
      <div class="chart-container">
        <v-chart
          class="echart-3d"
          :option="eabChartOption"
          ref="eabChartOption_Chart"
          :init-options="initOptions"
          autoresize
        />
      </div>
    </div>

    <div class="pdf-atomic-block">
      <!-- Brightness Contour - 静态图 -->
      <h5 class="sub-section-title">
        {{ $t("report.brightness_contour") }}
      </h5>
      <div class="chart-container">
        <img
          v-if="reportResultData.image_roi_url"
          class="analysis-image"
          :src="reportResultData.image_roi_url"
          crossorigin="anonymous"
          alt="Brightness Contour Chart"
        />
      </div>
    </div>

    <!-- ** 回到顶部按钮 ** -->
    <el-backtop
      v-if="!isPrinting"
      :target="backtopTarget"
      :right="40"
      :bottom="40"
    />
  </div>
</template>

<script>
import { backtopMixin } from "@/mixins/backtopMixin";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import {
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
} from "echarts/components";
import VChart from "vue-echarts";
import UTIF from "utif";
import "echarts-gl"; // 必须引入 echarts-gl

use([CanvasRenderer, GridComponent, TooltipComponent, VisualMapComponent]);

export default {
  name: "LuminanceUniformityReportDetail",
  mixins: [backtopMixin],
  components: { VChart },
  props: {
    reportData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      isPrinting: false,
      tableItemLabels: [
        { i18nKey: "report.sampling_max_brightness" },
        { i18nKey: "report.sampling_min_brightness" },
        { i18nKey: "report.center_brightness" },
        { i18nKey: "report.edge_min_brightness" },
        { i18nKey: "report.uniformity_sampling" },
        { i18nKey: "report.uniformity_edge" },
        { i18nKey: "report.uniformity_lin" },
        { i18nKey: "report.max_rg" },
        { i18nKey: "report.min_rg" },
        { i18nKey: "report.max_rb" },
        { i18nKey: "report.min_rb" },
        { i18nKey: "report.max_bg" },
        { i18nKey: "report.min_bg" },
        { i18nKey: "report.max_delta_eab" },
        { i18nKey: "report.mean_delta_eab" },
        { i18nKey: "report.max_delta_cab" },
        { i18nKey: "report.mean_delta_cab" },
        { i18nKey: "report.max_delta_e94" },
        { i18nKey: "report.mean_delta_e94" },
        { i18nKey: "report.max_delta_c94" },
        { i18nKey: "report.mean_delta_c94" },
        { i18nKey: "report.max_delta_e00" },
        { i18nKey: "report.mean_delta_e00" },
        { i18nKey: "report.max_delta_c00" },
        { i18nKey: "report.mean_delta_c00" },
        { i18nKey: "report.optical_axis_distance" },
        { i18nKey: "report.y_min" },
        { i18nKey: "report.y_max" },
        { i18nKey: "report.y_mean" },
        { i18nKey: "report.sigma" },
        { i18nKey: "report.standard_deviation" },
        // { i18nKey: "report.bright_spots" },
        // { i18nKey: "report.bright_spot_distance" },
        // { i18nKey: "report.dead_pixels" },
        // { i18nKey: "report.dead_pixel_distance" },
      ],
      initOptions: {
        renderer: "canvas",
        preserveDrawingBuffer: true,
      },
      activeSection: "test-report",
      tableData: [],
      gridChartOption: {},
      colorTemperatureChartOption: {},
      luminanceDistributionChartOption: {},
      convertedImageUrls: {
        original: null,
      },
      convertingMap: {
        original: false,
      },
      conversionErrors: {},
    };
  },
  computed: {
    reportResultData() {
      return this.reportData;
    },
    analysisTableData() {
      if (
        !this.reportData ||
        !this.reportData.result ||
        !this.reportData.result.analysis_result_form
      ) {
        return [];
      }
      const data = this.reportData.result.analysis_result_form;
      return this.tableItemLabels.map((label, index) => ({
        item: this.$t(label.i18nKey),
        value: data[index] !== undefined ? data[index] : "N/A",
      }));
    },
    luminanceTableData() {
      if (!this.reportData?.result?.luminance_uniformity_form) return [];
      return this.reportData.result.luminance_uniformity_form;
    },
    displayOriginalImage() {
      return this.getDisplayUrl("original", this.reportResultData?.image_url);
    },
    axisStyle() {
      return {
        axisLabel: {
          backgroundColor: "#fff",
          borderColor: "#26c6da",
          borderWidth: 1,
          borderRadius: 4,
          padding: [3, 5],
          color: "#333",
        },
        splitLine: {
          lineStyle: { color: "#D0EDED", width: 1 },
        },
        axisLine: {
          lineStyle: { color: "#26c6da" },
        },
      };
    },
    // Y(Luminance) Chart Option
    yLuminanceChartOption() {
      if (!this.reportResultData || !this.reportResultData.result) return {};
      return this.createChartOption(
        this.reportResultData.result.y_graph,
        (params) =>
          `X: ${params.data[0].toFixed(2)}<br/>Y: ${params.data[1].toFixed(
            2
          )}<br/>Luminance: ${params.data[2].toFixed(4)}`,
        ["#02245B", "#005B9A", "#00A896", "#4CAF50", "#D4E157"]
      );
    },
    // SNR(dB) Chart Option
    snrChartOption() {
      if (!this.reportResultData || !this.reportResultData.result) return {};
      return this.createChartOption(
        this.reportResultData.result.snr_graph,
        (params) =>
          `X: ${params.data[0].toFixed(2)}<br/>Y: ${params.data[1].toFixed(
            2
          )}<br/>SNR: ${params.data[2].toFixed(2)} dB`,
        ["#313695", "#4575b4", "#74add1", "#66bb6a", "#d4e157"]
      );
    },
    // Eab Chart Option
    eabChartOption() {
      if (!this.reportResultData || !this.reportResultData.result) return {};
      return this.createChartOption(
        this.reportResultData.result.eab_graph,
        (params) =>
          `X: ${params.data[0].toFixed(2)}<br/>Y: ${params.data[1].toFixed(
            2
          )}<br/>Eab: ${params.data[2].toFixed(2)}`,
        ["#011f4b", "#005b96", "#2E8B57", "#aee571", "#fdd835"]
      );
    },
  },
  watch: {
    "reportData.image_url": {
      handler(newUrl) {
        this.handleImageSourceChange("original", newUrl);
      },
      immediate: true,
    },
  },
  methods: {
    isTifUrl(url) {
      if (!url) return false;
      const lower = String(url).toLowerCase();
      return lower.endsWith(".tif") || lower.endsWith(".tiff");
    },
    getDisplayUrl(type, url) {
      if (!url) return "";
      const converted = this.convertedImageUrls[type];
      if (converted) {
        return converted;
      }
      if (!this.isTifUrl(url)) {
        return url;
      }
      if (!this.convertingMap[type]) {
        this.convertTifImage(type, url);
      }
      return "";
    },
    handleImageSourceChange(type, url) {
      this.$delete(this.conversionErrors, type);
      this.$set(this.convertedImageUrls, type, null);
      if (!url) {
        this.$set(this.convertingMap, type, false);
        return;
      }
      if (this.isTifUrl(url)) {
        this.convertTifImage(type, url);
      } else {
        this.$set(this.convertingMap, type, false);
        this.$set(this.convertedImageUrls, type, url);
      }
    },
    async convertTifImage(type, url) {
      if (!url || this.convertingMap[type]) {
        return;
      }
      this.$set(this.convertingMap, type, true);
      this.$delete(this.conversionErrors, type);
      try {
        const isMobile = window.innerWidth <= 768;
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), isMobile ? 30000 : 15000);
        
        const response = await fetch(url, {
          cache: "no-store",
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const ifds = UTIF.decode(arrayBuffer);
        if (!ifds.length) {
          throw new Error("No image data found in TIF file");
        }
        const firstImage = ifds[0];
        UTIF.decodeImage(arrayBuffer, firstImage);
        let { width, height } = firstImage;
        if (!width || !height) {
          throw new Error("Invalid TIF image size");
        }
        
        const maxCanvasSize = isMobile ? 2048 : 4096;
        let scale = 1;
        if (width > maxCanvasSize || height > maxCanvasSize) {
          scale = Math.min(maxCanvasSize / width, maxCanvasSize / height);
          width = Math.floor(width * scale);
          height = Math.floor(height * scale);
        }
        
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        
        if (!ctx) {
          throw new Error("无法创建canvas上下文");
        }
        
        const imageData = ctx.createImageData(width, height);
        const rgba = UTIF.toRGBA8(firstImage);
        
        if (scale < 1) {
          const originalWidth = firstImage.width;
          const originalHeight = firstImage.height;
          const invScale = 1 / scale;
          
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              const srcX = Math.min(Math.floor(x * invScale), originalWidth - 1);
              const srcY = Math.min(Math.floor(y * invScale), originalHeight - 1);
              const srcIdx = (srcY * originalWidth + srcX) * 4;
              const dstIdx = (y * width + x) * 4;
              imageData.data[dstIdx] = rgba[srcIdx];
              imageData.data[dstIdx + 1] = rgba[srcIdx + 1];
              imageData.data[dstIdx + 2] = rgba[srcIdx + 2];
              imageData.data[dstIdx + 3] = rgba[srcIdx + 3];
            }
          }
        } else {
          imageData.data.set(rgba);
        }
        
        ctx.putImageData(imageData, 0, 0);
        const dataUrl = canvas.toDataURL("image/png");
        
        canvas.width = 0;
        canvas.height = 0;
        
        // 使用$set确保响应式更新，并强制触发视图更新
        this.$set(this.convertedImageUrls, type, dataUrl);
        
        // 手机端：强制更新视图，确保图片显示
        const isMobileForUpdate = window.innerWidth <= 768;
        if (isMobileForUpdate) {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      } catch (error) {
        console.error(`Failed to convert TIF image (${type}):`, error);
        this.$set(
          this.conversionErrors,
          type,
          (error && error.message) || "convert failed"
        );
        this.$set(this.convertedImageUrls, type, null);
      } finally {
        this.$set(this.convertingMap, type, false);
      }
    },
    /**
     * @description 创建3D图表配置的辅助函数，减少代码重复
     * @param {object} graphObject - 包含 data 和 z_range 的图表数据对象
     * @param {Function} tooltipFormatter - ECharts tooltip 的 formatter 函数
     * @param {Array<string>} colorRange - visualMap 的颜色范围数组
     * @returns {object} ECharts option object
     */
    createChartOption(graphObject, tooltipFormatter, colorRange) {
      if (
        !graphObject ||
        !Array.isArray(graphObject.data) ||
        !Array.isArray(graphObject.z_range)
      ) {
        return {};
      }

      const { data, z_range } = graphObject;
      const [zMin, zMax] = z_range;

      return {
        tooltip: {
          formatter: tooltipFormatter,
        },
        visualMap: {
          min: zMin,
          max: zMax,
          inRange: {
            color: colorRange,
          },
          show: false,
        },
        xAxis3D: { type: "value", ...this.axisStyle },
        yAxis3D: { type: "value", ...this.axisStyle },
        zAxis3D: {
          type: "value",
          ...this.axisStyle,
          axisLabel: {
            ...this.axisStyle.axisLabel,
            formatter: (value) => value.toFixed(2),
          },
        },
        grid3D: this.getCommonGrid3DOptions(),
        series: [
          {
            type: "surface",
            data: data,
            wireframe: { show: false },
            shading: "lambert",
            itemStyle: { opacity: 0.95 },
          },
        ],
      };
    },
    togglePrintingState(isPrinting) {
      this.isPrinting = isPrinting;
    },
    getChartInstances() {
      const instances = [];
      if (this.$refs.yLuminanceChartOption_Chart) {
        instances.push(this.$refs.yLuminanceChartOption_Chart.chart);
      }
      if (this.$refs.snrChartOption_Chart) {
        instances.push(this.$refs.snrChartOption_Chart.chart);
      }
      if (this.$refs.eabChartOption_Chart) {
        instances.push(this.$refs.eabChartOption_Chart.chart);
      }
      return instances;
    },
    ensureChartsRendered() {
      const chartInstances = this.getChartInstances();
      if (!chartInstances || chartInstances.length === 0) {
        return Promise.resolve();
      }
      const promises = chartInstances.map(
        (chart) =>
          new Promise((resolve) => {
            const listener = () => {
              resolve();
              chart.off("finished", listener);
            };
            chart.on("finished", listener);
            setTimeout(() => {
              resolve();
              chart.off("finished", listener);
            }, 2000);
          })
      );
      return Promise.all(promises);
    },
    getCommonGrid3DOptions() {
      return {
        boxWidth: 120,
        boxDepth: 120,
        viewControl: {
          alpha: 30,
          beta: 35,
          distance: 266,
          autoRotate: false,
        },
        axisPointer: {
          show: true,
          lineStyle: { color: "#777", width: 1 },
        },
        light: {
          main: {
            intensity: 1.5,
            shadow: true,
            alpha: 45,
            beta: 55,
          },
          ambient: { intensity: 0.4 },
        },
      };
    },
    scrollToSection(sectionId) {
      const scrollContainer = this.$el.closest(
        ".detail-scrollbar .el-scrollbar__wrap"
      );
      if (!scrollContainer) return;
      if (sectionId === "top") {
        scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const targetElement = this.$el.querySelector(`#${sectionId}`);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    },
  },
};
</script>

<style scoped>
.custom-table ::v-deep .el-table__cell {
  padding: 8px 0;
}
.luminance-uniformity-report-detail-container {
  padding-right: 15px;
}
.report-header-image {
  margin-bottom: 25px;
}
.image-label-wrapper {
  margin-bottom: 12px;
  padding: 0 5px;
  text-align: left;
}
.image-wrapper {
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  padding: 12px;
  background-color: #fcfcfc;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}
.image-wrapper img {
  width: 100%;
  height: auto;
  max-height: 360px;
  object-fit: contain;
  border-radius: 4px;
  display: block;
}
.image-loading,
.image-error {
  width: 100%;
  text-align: center;
  color: #909399;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-loading .el-icon-loading {
  font-size: 24px;
  color: #409eff;
}
.image-error {
  color: #f56c6c;
}
.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-top: 35px;
  margin-bottom: 20px;
}
.sub-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-top: 25px;
  margin-bottom: 15px;
  padding-left: 12px;
  border-left: 4px solid #00a0e9;
}
.chart-container {
  width: 100%;
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  box-sizing: border-box;
  background-color: #fdfdfd;
}
.analysis-image {
  width: 100%;
  height: auto;
  border-radius: 4px;
  display: block;
  max-width: 450px;
  margin: 5px auto;
}
.echart-3d {
  height: 500px;
}

.analysis-table-wrapper {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}
.custom-table {
  margin-bottom: 20px;
}
.custom-table ::v-deep th {
  background-color: #f5f7fa !important;
  color: #606266;
  font-weight: 600;
}
.custom-table ::v-deep td,
.custom-table ::v-deep th {
  text-align: center;
}
@media (max-width: 768px) {
  .image-wrapper {
    max-width: 100%;
  }
}
</style>
